import React from "react";
import {
  Calendar as BigCalendar,
  Views,
  dateFnsLocalizer,
  type SlotInfo,
  type View,
  type DateCellWrapperProps,
  type DateHeaderProps,
} from "react-big-calendar";
import {
  format as formatDate,
  parse as parseDate,
  startOfWeek,
  getDay,
  parseISO,
  addDays,
  differenceInCalendarDays,
  startOfDay,
  startOfToday,
  addYears,
  isBefore,
  isAfter,
} from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./organization-job-offer-calendar.css";
import { Button } from "@/components/ui/button";
import { OfferOrganizationWageSetLabel } from "@/features/job-offer/components/offer-organization-wage-set-label";
import { wageFrequencyToEnding } from "@/features/job-offer/utils/wage-frequency-to-ending";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  DollarSign,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { startOfMonth } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  JobOfferPreviewDialog,
  type JobOfferPreviewData,
} from "./job-offer-preview-dialog";
import { getJobOfferOrgDisplayName } from "@/features/job-offer/utils/get-job-offer-org-display";

const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format: formatDate,
  parse: parseDate,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

export type OrganizationJobOffer = JobOfferPreviewData;

type CalendarEvent = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  status: JobOfferStatus;
  // Set when the event is a date that a pending proposal would add
  proposalId?: string;
  jobOffer: OrganizationJobOffer;
};

type OrganizationJobOfferCalendarProps = {
  jobOffers?: OrganizationJobOffer[];
  view: View;
  onViewChange: (view: View) => void;
  date: Date;
  onNavigate: (date: Date) => void;
  onCreateFromSelection: (params: {
    startDate: string;
    endDate: string;
    startTime?: string;
    endTime?: string;
  }) => void;
  onRequestEdit: (jobOfferId: string) => void;
  onCanceled?: () => void;
  user: { is_admin: boolean } | null | undefined;
};

type JobOfferStatus = "accepted" | "pending" | "canceled";

const getJobOfferStatus = (jobOffer: OrganizationJobOffer): JobOfferStatus => {
  if (jobOffer.canceledByOrganizationBusinessUnitAt) {
    return "canceled";
  }

  const acceptedDecision = jobOffer.decisions?.find(
    (decision) => decision.providerDecision === true,
  );

  if (acceptedDecision?.providerCanceledAt) {
    return "canceled";
  }

  if (acceptedDecision) {
    return "accepted";
  }

  return "pending";
};

const eventColors: Record<JobOfferStatus, string> = {
  accepted: "#15803d",
  pending: "var(--primary)",
  canceled: "#b91c1c",
};

const toDate = (value: Date | string) =>
  typeof value === "string" ? parseISO(value) : value;

// All-day events start and end at midnight
const isAllDayRange = (start: Date, end: Date) =>
  start.getHours() === 0 &&
  start.getMinutes() === 0 &&
  end.getHours() === 0 &&
  end.getMinutes() === 0;

const buildCalendarEvents = (
  jobOffers: OrganizationJobOffer[] | undefined,
): CalendarEvent[] => {
  if (!jobOffers) {
    return [];
  }

  const events: CalendarEvent[] = [];

  jobOffers.forEach((jobOffer) => {
    const status = getJobOfferStatus(jobOffer);

    // Skip canceled job offers
    if (status === "canceled") {
      return;
    }

    const title = getJobOfferOrgDisplayName(jobOffer).primaryName;

    jobOffer.shifts
      ?.filter((shift) => shift.status !== "canceled")
      .forEach((shift) => {
        const start = toDate(shift.startsAt);
        const end = toDate(shift.endsAt);
        events.push({
          id: `${jobOffer.id}-${shift.id}`,
          title,
          start,
          end,
          allDay: isAllDayRange(start, end),
          status,
          jobOffer,
        });
      });

    // Dates a pending proposal would add are not shifts until the proposal is
    // accepted, so show them as pending alongside the offer's shifts.
    jobOffer.pendingProposalShifts?.forEach((proposedShift) => {
      const start = toDate(proposedShift.startsAt);
      const end = toDate(proposedShift.endsAt);
      events.push({
        id: `${jobOffer.id}-${proposedShift.proposalId}-${start.toISOString()}`,
        title,
        start,
        end,
        allDay: isAllDayRange(start, end),
        status: "pending",
        proposalId: proposedShift.proposalId,
        jobOffer,
      });
    });
  });

  return events;
};

const getEventStatusLabel = (event: CalendarEvent): string => {
  if (event.proposalId) {
    return "Pending proposal";
  }
  if (event.status === "accepted") {
    return "Accepted";
  }
  if (event.status === "canceled") {
    return "Canceled";
  }
  return "Waiting for provider";
};

// A proposed date lists the proposal's dates; a shift lists the offer's schedule
const getTooltipSchedule = (event: CalendarEvent) => {
  if (event.proposalId) {
    return {
      heading: "Proposed dates",
      shifts: (event.jobOffer.pendingProposalShifts ?? []).filter(
        (shift) => shift.proposalId === event.proposalId,
      ),
    };
  }
  return {
    heading: "Schedule",
    shifts: (event.jobOffer.shifts ?? []).filter(
      (shift) => shift.status !== "canceled",
    ),
  };
};

// Shared tooltip content for both event components
const EventTooltipContent: React.FC<{ event: CalendarEvent }> = ({ event }) => {
  const { status } = event;
  const statusLabel = getEventStatusLabel(event);
  const schedule = getTooltipSchedule(event);
  const statusBadgeClass =
    status === "accepted"
      ? "bg-emerald-100 text-emerald-700"
      : status === "canceled"
        ? "bg-red-100 text-red-700"
        : "bg-primary/10 text-primary";

  return (
    <TooltipContent
      className="max-w-xs p-0 overflow-hidden bg-white border border-slate-200 rounded-lg shadow-lg"
      sideOffset={8}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900 truncate">
              {event.jobOffer.title || "Job Offer"}
            </p>
            {event.jobOffer.facility?.name && (
              <p className="text-xs text-slate-500 truncate mt-0.5">
                {event.jobOffer.facility.name}
              </p>
            )}
          </div>
          <span
            className={`text-[10px] font-medium px-2 py-0.5 rounded-full uppercase shrink-0 ${statusBadgeClass}`}
          >
            {statusLabel}
          </span>
        </div>
      </div>
      {/* Schedule */}
      {schedule.shifts.length > 0 && (
        <div className="px-4 py-3 bg-slate-50">
          <div className="flex items-center gap-1.5 mb-2">
            <CalendarDays className="h-3.5 w-3.5 text-primary" />
            <span className="text-[10px] font-medium uppercase text-slate-400 tracking-wide">
              {schedule.heading}
            </span>
          </div>
          <ul className="space-y-1.5">
            {schedule.shifts.slice(0, 2).map((shift, index) => (
              <li
                key={`${event.jobOffer.id}-tt-${index}`}
                className="text-xs text-slate-600"
              >
                {formatShiftDisplay(shift)}
              </li>
            ))}
            {schedule.shifts.length > 2 && (
              <li className="text-xs text-slate-400">
                +{schedule.shifts.length - 2} more
              </li>
            )}
          </ul>
        </div>
      )}
      {/* Wage */}
      {event.jobOffer.regularWage && (
        <div className="px-4 py-3 border-t border-slate-100">
          <div className="flex items-center gap-1.5">
            <DollarSign className="h-3.5 w-3.5 text-green-500" />
            <span className="text-sm text-slate-600">
              <span className="font-medium text-slate-800">
                ${event.jobOffer.regularWage}
              </span>
              {event.jobOffer.wageFrequencyId && (
                <span className="text-slate-400">
                  {wageFrequencyToEnding[event.jobOffer.wageFrequencyId]}
                </span>
              )}
            </span>
          </div>
          <OfferOrganizationWageSetLabel
            organizationWageSetName={event.jobOffer.organizationWageSetName}
            className="mt-1 text-xs text-slate-500"
          />
        </div>
      )}
    </TooltipContent>
  );
};

const MonthEventComponent: React.FC<{ event: CalendarEvent }> = ({ event }) => {
  const { status } = event;
  const timeLabel = formatDate(event.start, "h:mma").toLowerCase();
  const displayInfo = getJobOfferOrgDisplayName(event.jobOffer);

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 min-w-0">
            <span
              className={cn(
                "h-2 w-2 rounded-full shrink-0",
                status === "accepted" && "bg-green-600",
                status === "pending" &&
                  "border border-dashed border-primary bg-primary/10",
                status === "canceled" && "bg-red-600",
              )}
            />
            <span className="text-[11px] text-slate-600 shrink-0">
              {timeLabel}
            </span>
            <span
              className="text-[11px] font-medium text-slate-800 truncate"
              title={displayInfo.tooltip}
            >
              {displayInfo.primaryName}
            </span>
          </div>
        </TooltipTrigger>
        <EventTooltipContent event={event} />
      </Tooltip>
    </TooltipProvider>
  );
};

const WeekDayEventComponent: React.FC<{ event: CalendarEvent }> = ({
  event,
}) => {
  const displayInfo = getJobOfferOrgDisplayName(event.jobOffer);

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-col h-full">
            <span
              className="font-semibold text-xs leading-tight truncate"
              title={displayInfo.tooltip}
            >
              {displayInfo.primaryName}
            </span>
            {displayInfo.secondaryInfo && (
              <span className="text-[10px] leading-tight truncate opacity-80">
                {displayInfo.secondaryInfo}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <EventTooltipContent event={event} />
      </Tooltip>
    </TooltipProvider>
  );
};

const formatShiftDisplay = (shift: {
  startsAt: Date | string;
  endsAt: Date | string;
}) => {
  const start =
    typeof shift.startsAt === "string"
      ? parseISO(shift.startsAt)
      : shift.startsAt;
  const end =
    typeof shift.endsAt === "string" ? parseISO(shift.endsAt) : shift.endsAt;

  const startDateLabel = formatDate(start, "MMM d, yyyy");
  const endDateLabel = formatDate(end, "MMM d, yyyy");

  const dateLabel =
    startDateLabel === endDateLabel
      ? startDateLabel
      : `${startDateLabel} – ${endDateLabel}`;

  // Check if it's an all-day event (both start and end at midnight)
  const isAllDay =
    start.getHours() === 0 &&
    start.getMinutes() === 0 &&
    end.getHours() === 0 &&
    end.getMinutes() === 0;

  if (isAllDay) {
    return dateLabel;
  }

  const startTimeLabel = formatDate(start, "h:mm a");
  const endTimeLabel = formatDate(end, "h:mm a");
  const timeLabel = `${startTimeLabel} – ${endTimeLabel}`;

  return `${dateLabel} • ${timeLabel}`;
};

export const OrganizationJobOfferCalendar: React.FC<
  OrganizationJobOfferCalendarProps
> = ({
  jobOffers,
  view,
  onViewChange,
  date,
  onNavigate,
  onCreateFromSelection,
  onRequestEdit,
  onCanceled,
  user,
}) => {
  const viewControlId = React.useId();
  const events = React.useMemo(
    () => buildCalendarEvents(jobOffers),
    [jobOffers],
  );
  const today = React.useMemo(() => startOfToday(), []);
  const maxDate = React.useMemo(() => addYears(today, 1), [today]);
  const [selectedEvent, setSelectedEvent] =
    React.useState<CalendarEvent | null>(null);

  const calendarHeight = React.useMemo(() => {
    switch (view) {
      case Views.DAY:
        return 900;
      case Views.WEEK:
        return 750;
      default:
        return 640;
    }
  }, [view]);

  const monthOptions = React.useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => ({
        value: index,
        label: formatDate(startOfMonth(new Date(2024, index, 1)), "MMMM"),
      })),
    [],
  );

  const yearOptions = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 5;
    const endYear = currentYear + 5;
    return Array.from({ length: endYear - startYear + 1 }, (_, index) => {
      const year = startYear + index;
      return { value: year, label: String(year) };
    });
  }, []);

  const MonthDateHeader = React.useMemo(
    () =>
      function MonthDateHeaderComponent({
        date: headerDate,
        label,
        isOffRange,
        drilldownView,
        onDrillDown,
      }: DateHeaderProps) {
        const isTodayDate = headerDate.toDateString() === today.toDateString();
        const handleDrillDown = React.useCallback(
          (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            if (drilldownView && onDrillDown) {
              onDrillDown();
            }
          },
          [drilldownView, onDrillDown],
        );

        return (
          <div className={cn("py-1", isOffRange && "opacity-50")}>
            <button
              type="button"
              onClick={handleDrillDown}
              className={cn(
                "inline-flex items-center justify-center rounded-full text-sm font-medium transition-colors",
                isTodayDate
                  ? "bg-primary text-primary-foreground h-7 w-7"
                  : "text-slate-700 hover:text-primary",
              )}
            >
              {label}
            </button>
          </div>
        );
      },
    [today],
  );

  const DateCellWrapper = React.useMemo(
    () =>
      function DateCellWrapperComponent({
        children,
        value,
      }: DateCellWrapperProps) {
        const child = React.Children.only(children) as React.ReactElement<{
          className?: string;
          children?: React.ReactNode;
        }>;

        if (view !== Views.MONTH) {
          return child;
        }

        const className =
          [child.props.className, "group relative pointer-events-auto"]
            .filter(Boolean)
            .join(" ") || undefined;

        return React.cloneElement(child, {
          className,
          children: (
            <>
              {child.props.children}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 transition group-hover:bg-primary/5" />
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (isBefore(value, today) || isAfter(value, maxDate)) {
                      toast("Pick a date within the next year", {
                        description:
                          "Job offers must be scheduled for today or up to one year out.",
                      });
                      return;
                    }
                    onCreateFromSelection({
                      startDate: formatDate(value, "yyyy-MM-dd"),
                      endDate: formatDate(value, "yyyy-MM-dd"),
                    });
                  }}
                  aria-label={`Create job offer on ${formatDate(value, "MMMM d, yyyy")}`}
                  className="absolute inset-x-2 bottom-2 hidden pointer-events-auto items-center justify-center gap-2 rounded-md bg-primary px-2 py-1 text-[11px] font-medium text-white shadow-sm transition group-hover:flex focus-visible:flex focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                >
                  <Plus className="h-3 w-3" />
                  Add Job Offer
                </button>
              </div>
            </>
          ),
        });
      },
    [maxDate, onCreateFromSelection, today, view],
  );

  const handleSelectSlot = React.useCallback(
    (slotInfo: SlotInfo) => {
      if (!slotInfo.start) {
        return;
      }

      const slots = Array.isArray(slotInfo.slots) ? slotInfo.slots : [];
      const lastSlot =
        slots.length > 0 ? (slots[slots.length - 1] as Date) : slotInfo.end;
      const normalizedEnd = lastSlot ?? slotInfo.start;
      const slotStart = startOfDay(slotInfo.start);
      const slotEnd = startOfDay(normalizedEnd);

      if (isBefore(slotStart, today) || isAfter(slotEnd, maxDate)) {
        toast("Pick a date within the next year", {
          description:
            "Job offers must be scheduled for today or up to one year out.",
        });
        return;
      }

      const isAllDaySelection =
        view === Views.MONTH ||
        (!slotInfo.start.getHours() &&
          !normalizedEnd.getHours() &&
          differenceInCalendarDays(normalizedEnd, slotInfo.start) >= 0);

      const startDate = formatDate(slotInfo.start, "yyyy-MM-dd");
      const endDate = formatDate(
        isAllDaySelection ? normalizedEnd : (slotInfo.end ?? slotInfo.start),
        "yyyy-MM-dd",
      );

      const selectionPayload: {
        startDate: string;
        endDate: string;
        startTime?: string;
        endTime?: string;
        isAllDay: boolean;
      } = {
        startDate,
        endDate,
        isAllDay: isAllDaySelection,
      };

      if (!isAllDaySelection) {
        selectionPayload.startTime = formatDate(slotInfo.start, "HH:mm");

        // Only set end time if user explicitly selected a range longer than the default 30-min slot
        // A single click results in a 30-minute selection, so we treat that as "no end time selected"
        if (slotInfo.end) {
          const durationMs = slotInfo.end.getTime() - slotInfo.start.getTime();
          const durationMinutes = durationMs / (1000 * 60);

          console.log("[Calendar] Selection:", {
            start: slotInfo.start,
            end: slotInfo.end,
            durationMinutes,
            slots: slots.length,
          });

          // If duration is more than 30 minutes, user dragged to select a range
          if (durationMinutes > 30) {
            selectionPayload.endTime = formatDate(slotInfo.end, "HH:mm");
          }
        }
      }

      console.log("[Calendar] Payload:", selectionPayload);

      onCreateFromSelection({
        startDate: selectionPayload.startDate,
        endDate: selectionPayload.endDate,
        ...(selectionPayload.startTime
          ? { startTime: selectionPayload.startTime }
          : {}),
        ...(selectionPayload.endTime
          ? { endTime: selectionPayload.endTime }
          : {}),
      });
    },
    [maxDate, onCreateFromSelection, today, view],
  );

  return (
    <>
      <div className="w-full">
        <div className="job-offer-calendar min-w-0">
          {/* Custom Toolbar */}
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 px-3 sm:px-4 py-3">
            {/* Top row: navigation and date selectors */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex h-12 shrink-0 items-center rounded-xl border border-slate-200 bg-white p-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    onNavigate(
                      addDays(
                        date,
                        view === Views.MONTH
                          ? -30
                          : view === Views.WEEK
                            ? -7
                            : -1,
                      ),
                    )
                  }
                  className="h-10 w-10 rounded-lg p-0 text-slate-600 hover:bg-slate-100 hover:text-primary"
                  aria-label="Go to previous period"
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onNavigate(new Date())}
                  className="h-10 rounded-lg px-3 text-sm font-medium text-slate-700 hover:bg-slate-100 hover:text-primary"
                >
                  Today
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    onNavigate(
                      addDays(
                        date,
                        view === Views.MONTH ? 30 : view === Views.WEEK ? 7 : 1,
                      ),
                    )
                  }
                  className="h-10 w-10 rounded-lg p-0 text-slate-600 hover:bg-slate-100 hover:text-primary"
                  aria-label="Go to next period"
                >
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
              <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto">
                <Select
                  value={String(date.getMonth())}
                  onValueChange={(value) => {
                    const month = Number(value);
                    const next = startOfMonth(
                      new Date(date.getFullYear(), month, 1),
                    );
                    onNavigate(next);
                  }}
                >
                  <SelectTrigger
                    aria-label="Calendar month"
                    className="min-w-0 flex-1 sm:w-36 sm:flex-none"
                  >
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={String(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={String(date.getFullYear())}
                  onValueChange={(value) => {
                    const year = Number(value);
                    const next = startOfMonth(
                      new Date(year, date.getMonth(), 1),
                    );
                    onNavigate(next);
                  }}
                >
                  <SelectTrigger
                    aria-label="Calendar year"
                    className="w-24 shrink-0"
                  >
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {yearOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={String(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <fieldset className="grid h-12 w-full grid-cols-3 rounded-xl border border-slate-200 bg-slate-100 sm:ml-auto sm:w-auto">
                <legend className="sr-only">Calendar view</legend>
                {[Views.MONTH, Views.WEEK, Views.DAY].map((calendarView) => (
                  <label
                    key={calendarView}
                    className="relative flex h-full min-w-0 cursor-pointer select-none p-1"
                  >
                    <input
                      type="radio"
                      name={`${viewControlId}-calendar-view`}
                      value={calendarView}
                      checked={view === calendarView}
                      onChange={() => onViewChange(calendarView)}
                      className="peer sr-only"
                    />
                    <span className="flex min-w-0 flex-1 items-center justify-center rounded-lg px-3 text-sm font-medium capitalize text-slate-600 transition-colors hover:text-slate-900 peer-checked:bg-white peer-checked:text-primary peer-checked:shadow-xs peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-slate-100">
                      {calendarView.toLowerCase()}
                    </span>
                  </label>
                ))}
              </fieldset>
            </div>
            {/* Bottom row: date context and legend */}
            <div className="flex items-center justify-between">
              {/* Date context label for week/day views */}
              <div className="text-xs sm:text-sm font-medium text-slate-600">
                {view === Views.WEEK && (
                  <span>
                    Week of{" "}
                    {formatDate(
                      startOfWeek(date, { weekStartsOn: 0 }),
                      "MMM d",
                    )}
                  </span>
                )}
                {view === Views.DAY && (
                  <span>{formatDate(date, "EEE, MMM d, yyyy")}</span>
                )}
              </div>
              {/* Status Legend */}
              <div className="hidden sm:flex items-center gap-3 text-xs text-slate-500">
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-green-600" />
                  <span>Accepted</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full border-2 border-dashed border-primary bg-primary/10" />
                  <span>Pending</span>
                </div>
              </div>
            </div>
          </div>
          <BigCalendar
            localizer={localizer}
            events={events}
            views={[Views.MONTH, Views.WEEK, Views.DAY]}
            view={view}
            onView={onViewChange}
            date={date}
            onNavigate={onNavigate}
            selectable
            startAccessor="start"
            endAccessor="end"
            showAllEvents
            onSelectSlot={handleSelectSlot}
            onSelectEvent={(event) => setSelectedEvent(event as CalendarEvent)}
            eventPropGetter={(event) => {
              const { status } = event;
              const isAccepted = status === "accepted";
              const baseColor = eventColors[status];

              // Month view: minimal styling, let the component handle the look
              if (view === Views.MONTH) {
                return {
                  style: {
                    backgroundColor: "transparent",
                    border: "none",
                    padding: "1px 4px",
                    fontSize: "0.6875rem",
                    fontWeight: 400,
                    borderRadius: "2px",
                  },
                };
              }

              // Week/Day view: keep the colored block style
              const lightBackground = `color-mix(in srgb, ${baseColor} 8%, transparent)`;
              const borderColor =
                status === "pending"
                  ? "color-mix(in srgb, var(--primary) 50%, transparent)"
                  : status === "canceled"
                    ? "rgba(185, 28, 28, 0.5)"
                    : baseColor;
              return {
                style: {
                  backgroundColor: isAccepted ? baseColor : lightBackground,
                  borderRadius: "0.375rem",
                  color: isAccepted ? "#fff" : baseColor,
                  border: isAccepted ? "none" : `2px dashed ${borderColor}`,
                  padding: "4px 6px",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                },
              };
            }}
            components={{
              event:
                view === Views.MONTH
                  ? MonthEventComponent
                  : WeekDayEventComponent,
              toolbar: () => null,
              month: {
                dateHeader: MonthDateHeader,
              },
              dateCellWrapper: DateCellWrapper,
            }}
            style={{ height: calendarHeight, minHeight: 480 }}
          />
        </div>
      </div>
      <JobOfferPreviewDialog
        jobOffer={selectedEvent?.jobOffer ?? null}
        open={!!selectedEvent}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedEvent(null);
          }
        }}
        onRequestEdit={(jobOfferId) => {
          onRequestEdit(jobOfferId);
          setSelectedEvent(null);
        }}
        onCanceled={() => {
          setSelectedEvent(null);
          onCanceled?.();
        }}
        user={user}
      />
    </>
  );
};
